import * as React from "react";
import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";
import { AntDTable } from "@/common/components/ui";
import { Card, CardContent } from "@/common/components/ui/card";
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  Layers,
  Eye,
  Download,
} from "lucide-react";
import { Switch, TableColumnsType, message } from "antd";
import finEaseHomeNavigationRoutes from "@/projects/FinEaseHomeAndAdmin/utils/navigationRoutes";
import ScreenerService from "@/services/FinEaseHomeAndAdmin/APIs/ScrennerService";
import { QueryMaster } from "@/utils/types/Screener/Screener";
import DeleteConfirmationModal from "@/projects/FinEaseHomeAndAdmin/components/DeleteConfirmationModal";
import * as XLSX from "xlsx";
import { notify } from "@/common/components/common/Notification";

const QueryMasterAdminList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [enabledFilter, setEnabledFilter] = useState(true);

  // API Integration States
  const [queries, setQueries] = useState<QueryMaster[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<QueryMaster[]>([]);

  // Delete State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchQueries = async (
    currentPage = 1,
    currentSize = 10,
    search = ""
  ) => {
    setIsLoading(true);
    try {
      const response = await ScreenerService.getQueryMasters(
        currentPage,
        currentSize,
        search
      );
      if (response && response.success !== false) {
        setQueries(response.data.results ?? []);
        setTotal(response.data.count ?? 0);
      } else {
        message.warning("Failed to fetch admin queries");
      }
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message ??
          "Something went wrong while fetching admin queries."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchQueries(page, pageSize, searchTerm);
  }, [page, pageSize, searchTerm]);

  const handleDelete = (query: QueryMaster) => {
    setItemToDelete({ id: query.id, name: query.title });
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await ScreenerService.deleteQueryMasterById(
        itemToDelete.id
      );
      if (response && response.success !== false) {
        message.success("Admin query deleted successfully");
        fetchQueries(page, pageSize, searchTerm);
      } else {
        message.warning(response.message ?? "Failed to delete admin query");
      }
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message ??
          "Something went wrong while deleting admin query."
      );
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
    }
  };

  const columns: TableColumnsType<QueryMaster> = useMemo(
    () => [
      {
        title: "Title",
        dataIndex: "title",
        key: "title",
        render: text => <p className="font-bold text-secondary">{text}</p>,
      },
      {
        title: "Sub Title",
        dataIndex: "sub_title",
        key: "sub_title",
      },
      {
        title: "Category",
        dataIndex: "category_title",
        key: "category_title",
      },
      {
        title: "Status",
        dataIndex: "is_enabled",
        key: "is_enabled",
        width: 150,
        render: (enabled: boolean) => (
          <span
            className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
              enabled
                ? "bg-green-100 text-green-700"
                : "bg-red-100 text-red-700"
            }`}
          >
            {enabled ? "Enabled" : "Disabled"}
          </span>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        fixed: "right" as const,
        width: 150,
        align: "center",
        render: (_, query) => (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() =>
                navigate(
                  finEaseHomeNavigationRoutes.QUERY_MASTER_ADMIN_VIEW(query.id)
                )
              }
              className="h-8 w-8 rounded-lg text-slate-500 hover:bg-brandPrimary/10 hover:text-brandPrimary"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() =>
                navigate(
                  finEaseHomeNavigationRoutes.QUERY_MASTER_ADMIN_EDIT(query.id)
                )
              }
              className="h-8 w-8 rounded-lg text-slate-500 hover:bg-brandPrimary/10 hover:text-brandPrimary"
            >
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDelete(query)}
              className="h-8 w-8 rounded-lg text-red-600 hover:bg-brandPrimary/10 hover:text-brandPrimary"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [navigate]
  );

  const filteredQueries = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    let list = queries;
    if (normalizedSearch) {
      list = list.filter(query =>
        String(query.title ?? "")
          .toLowerCase()
          .includes(normalizedSearch)
      );
    }

    if (enabledFilter) {
      list = list.filter(query => query.is_enabled);
    }

    return list;
  }, [queries, searchTerm, enabledFilter]);

  const onSelectChange = (
    newSelectedRowKeys: React.Key[],
    newSelectedRows: QueryMaster[]
  ) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(newSelectedRows);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleExport = () => {
    if (selectedRows.length === 0) {
      notify.error("No Data", "Please select items to export.");
      return;
    }

    const rows = selectedRows.map(query => ({
      ID: query.id,
      Title: query.title,
      "Sub Title": query.sub_title ?? "",
      Category: (query as any).category_title ?? "",
      Enabled: query.is_enabled ? "Yes" : "No",
      "Created On": query.created_on
        ? new Date(query.created_on).toLocaleDateString("en-IN")
        : "",
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Admin Queries");

    const datePart = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `query_master_admin_${datePart}.xlsx`);
    notify.success("Exported", `${rows.length} query(s) exported.`);
  };

  return (
    <div className="space-y-6 p-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 md:p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
        <svg
          className="absolute right-0 top-0 h-full w-1/3 text-white/5"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon fill="currentColor" points="50,0 100,0 100,100 0,100" />
        </svg>
        <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
              <Layers className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-white">
                Query Master Admin
              </h1>
              <p className="text-white/70">Manage admin queries here.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={handleExport}
              disabled={selectedRows.length === 0}
            >
              <Download className="h-4 w-4" />
              Export
              {selectedRows.length > 0 && (
                <span className="ml-1 rounded-full bg-white/25 px-1.5 py-0.5 text-[11px] font-semibold leading-none">
                  {selectedRows.length}
                </span>
              )}
            </Button>
            <Button
              onClick={() =>
                navigate(finEaseHomeNavigationRoutes.QUERY_MASTER_ADMIN_ADD)
              }
              className="gap-2 bg-white font-semibold text-secondary hover:bg-white/90"
            >
              <Plus className="h-4 w-4" /> Add Admin Query
            </Button>
          </div>
        </div>
      </div>

      <Card className="rounded-xl border-none shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search queries by title..."
                className="h-11 rounded-xl border-slate-200 bg-gray-50 pl-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-gray-50 px-4 py-2.5">
              <span className="text-sm font-semibold text-slate-700">
                {enabledFilter ? "Show Enabled" : "Show All"}
              </span>
              <Switch
                checked={enabledFilter}
                onChange={checked => setEnabledFilter(checked)}
                style={{
                  backgroundColor: enabledFilter ? "#66A0CD" : "#9ca3af",
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div>
        <AntDTable
          showSettings
          data={filteredQueries}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          rowSelection={rowSelection}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            onChange: (p, size) => {
              setPage(p);
              setPageSize(size);
            },
          }}
          emptyText="No queries found"
        />
      </div>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Admin Query"
        message="Are you sure you want to delete"
        itemName={itemToDelete?.name ?? ""}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default QueryMasterAdminList;
